import {
  AfterViewInit, Component, ComponentRef, EventEmitter, ElementRef, HostBinding, Input,
  Inject, Output, OnChanges, OnInit, Renderer2, SimpleChanges, Type, ViewContainerRef,
  ViewChild, ChangeDetectorRef, ChangeDetectionStrategy,
} from '@angular/core';
import * as _ from 'lodash';
import * as moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { forkJoin } from 'rxjs';


import { segments } from './ecommerce-segments';
import { getFieldByMap, getItemProperty } from 'utils/utils';
import { segmentsData } from '../product-details/segments-data-stub';
import * as productHelpers from 'shared/product/product-helpers';
import { publishedToast, unpublishedToast } from 'shared/toaster/toasts';
import { productPreviewOptions } from '../product-details/preview-options';
import { setItemDetailValidation } from 'shared/item-detail/item-detail-validation';
import * as searchPageHelpers from 'shared/search-page/search-page.helpers';
import { ECOMMERCE_PANEL } from '../product-panel/product-panel-types';
import { indexPagesData } from 'utils/index-pages-data';
import { ecommerceFields } from './ecommerce-fields';
import { SideNavButtonModel } from 'shared/status-sidenav/status-sidenav/side-nav.model';
import { ReturnRouteModel } from 'shared/return-route/return-route.model';

import { SegmentsNavigationComponent } from 'shared/segments-navigation/segments-navigation/segments-navigation.component';
import { BaseRoutingPageComponent } from 'shared/base/_base-routing-page.component';
import { ProductPanelComponent } from '../product-panel/product-panel.component';
import { LoaderComponent } from 'shared/loader/loader.component';

import { DynamicComponentLoader } from 'shared/base/dynamic-component-loader.service';
import { ItemDetailService } from 'shared/item-detail/item-detail.service';
import { BrandService } from 'shared/brand/brand.service';
import { PriceSheetService } from 'shared/price-sheet/price-sheet.service';
import { MarketingCopyDetailsService } from 'shared/marketing-copy/marketing-copy-details.service';
import { ReturnRouteService } from 'shared/return-route/return-route.service';
import { PermissionsService } from 'shared/permissions/permissions.service';
import { PreferencesService } from 'shared/preferences/preferences.service';

import { ConvertUTCToTZPipe } from 'shared/pipes/convert-utc-to-tz.pipe';

import {
  PRODUCT_STATUS_WORKING,
  PRODUCT_STATUS_REVIEW,
  PRODUCT_STATUS_APPROVED
} from 'shared/product/product-statuses';
import {first, takeWhile} from 'rxjs/operators';


@Component({
  moduleId: module.id,
  selector: 'ecommerce-panel',
  templateUrl: 'ecommerce.component.html',
  styleUrls: ['ecommerce.component.scss'],
  providers: [DynamicComponentLoader],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EcommerceComponent extends BaseRoutingPageComponent implements OnInit, AfterViewInit {

  @ViewChild('segmentContainer', { read: ViewContainerRef, static: false })
  segmentContainer: ViewContainerRef | void;

  @ViewChild(SegmentsNavigationComponent, {static: false})
  segmentsNavigation: SegmentsNavigationComponent | void;

  @ViewChild('scroll', {static: false})
  scroll: ElementRef | void;

  @ViewChild(ProductPanelComponent, {static: true})
  productPanel: ProductPanelComponent | void;

  @ViewChild(LoaderComponent, {static: false})
  loader: LoaderComponent | void;

  itemDetail: any;
  priceSheets: Array<any>;
  marketingCopy: any = {};
  dropdownPreviewOptions: Array<any> = productPreviewOptions;
  navigationSegments = segments;
  selectedLanguage: string = 'EN';
  sideNavStatus: string = '';
  productImageUrl: string = '';
  sideNavTopButton: SideNavButtonModel = new SideNavButtonModel();
  sideNavBottomButton: SideNavButtonModel = new SideNavButtonModel();
  panelType: string = ECOMMERCE_PANEL;
  isFirstLoad: boolean = true;
  headerColor: string = '';
  isItemIvalid: boolean = false;
  prevRouteShort: string = '';

  private componentRef: ComponentRef<any>;
  private timezone: any;

  get updatedDate(): string {
    if (!this.itemDetail || !_.get(this, 'itemDetail.updated_at')) {
      return '';
    }
    const dateTZ = this.convertUTCToTZPipe.transform(this.itemDetail.updated_at, this.timezone);
    return moment(dateTZ).format('MMMM Do, YYYY');
  }

  get lastPublishedDate(): string {
    if (!this.itemDetail || !_.get(this, 'itemDetail.last_published_at')) {
      return '';
    }
    const dateTZ = this.convertUTCToTZPipe.transform(this.itemDetail.last_published_at, this.timezone);
    return moment(dateTZ).format('MMMM Do, YYYY');
  }

  get description(): string {
    return productHelpers.getHeaderDescription(this.itemDetail);
  }

  get itemId(): any {
    return _.get(this.route.snapshot.params, 'id', '');
  }

  get brandId(): string {
    return this.brandService.getBrand();
  }

  get showEdit(): boolean {
    if (!this.permissionsService.hasPermission(['admin', 'catalog-edit'])) {
      return false;
    }

    const status = _.get(this.itemDetail, 'status');

    if (_.indexOf([
      PRODUCT_STATUS_WORKING,
      PRODUCT_STATUS_REVIEW,
      PRODUCT_STATUS_APPROVED
    ], status) >= 0 ) {
        return true;
    } else {
      return false;
    }
  }

  constructor(
    private _renderer: Renderer2,
    private dynamicComponentLoader: DynamicComponentLoader,
    private itemDetailService: ItemDetailService,
    private brandService: BrandService,
    private priceSheetService: PriceSheetService,
    private toastrService: ToastrService,
    private marketingCopyDetailsService: MarketingCopyDetailsService,
    private route: ActivatedRoute,
    protected router: Router,
    private ref: ChangeDetectorRef,
    private permissionsService: PermissionsService,
    private preferencesService: PreferencesService,
    private convertUTCToTZPipe: ConvertUTCToTZPipe,
    protected returnRouteService: ReturnRouteService,
  ) {
    super(router);
  }

  /*******************************************
   * COMPONENT HOOKS
   *******************************************/

  ngOnInit() {
    /*
     * Protect this page with permissions, `admin`, `catalog-view`, `catalog-edit`, 'repository'.
     */
    if (!this.permissionsService.hasPermission(['admin', 'catalog-view', 'catalog-edit', 'repository']) ) {
         this.router.navigate(['/logout']);
     }

    super.ngOnInit();
    this.getItemDetail(this.brandId, this.itemId);
    if (this.productPanel) {
      this.productPanel.onModalHidden
        .pipe(takeWhile(() => this.isPageActive))
        .subscribe(() => {
          this.getItemDetail(this.brandId, this.itemId);
        });
    }
    // set active ecommerce-view preview option
    _.each(this.dropdownPreviewOptions, (item: any) => {
      item.active = item.route === '/ecommerce-view';
    });

    this.returnRouteSetup();

    if (this.route.snapshot.queryParams['panel']) {
      this.toggleProductPanel(true);
    }

    // get timezone
    this.preferencesService.getPreferences()
    .subscribe((preferences: any) => {
      this.timezone = preferences.timezone;
    });
  }

  ngAfterViewInit() {
    const segment = _.get(this.route.snapshot.params, 'segment', '');
    if (this.segmentsNavigation) {
      this.segmentsNavigation.setActive(segment);
    }
  }

  /*******************************************
   * COMPONENT HANDLERS
   *******************************************/

  onRouteChange(event: NavigationEnd) {
    const segment = this.getCurrentSegment();
    if (!segment) {
      const segmentName = this.route.snapshot.url[2].path;
      console.warn(`segment ${segmentName} not found`);
      console.warn(`segment ${event} not found`);
      return;
    }
    _.defer(() => {
      if (!this.isFirstLoad) {
        this.renderSegment(segment.component);
      }
    });
  }

  onOptionSelect($event: any) {
    if ($event.route !== '/ecommerce-preview') {
      this.router.navigate([$event.route, this.itemId]);
    }
  }

  onEditPanelClick() {
    this.toggleProductPanel(true);
  }

  onSelectSegment(item: any) {
    this.router.navigate([`../${item.segment}`], { relativeTo: this.route });
  }

  onLanguageSelect(languageCode: string) {
    this.selectedLanguage = languageCode;
    this.componentRef.instance.selectedLanguage = languageCode;
  }

  onEcommerceClose() {
    this.closePage();
  }

  onSideNavButtonClicked(value: any) {
    if (value === 5) {
      if (this.loader) {
        this.loader.show();
      }
      this.itemDetailService.postAcceptImported(this.brandId, this.itemId)
        .subscribe( (itemDetail) => {
          this.setData(itemDetail);
          this.populateSideNavValues(this.itemDetail.status);
          if (this.loader) {
            this.loader.hide();
          }
          this.ref.markForCheck();
        });
    } else {
      if (this.loader) {
        this.loader.show();
      }
      this.itemDetailService.postUpdateStatus(this.brandId, this.itemId, value)
        .subscribe( (itemDetail) => {
          this.setData(itemDetail);
          this.populateSideNavValues(this.itemDetail.status);
          if (value === 8) {
            this.toastrService.info('', '', publishedToast());
          }
          if (value === 10) {
            this.toastrService.info('', '', unpublishedToast());
            this.closePage();
          }
          if (this.loader) {
            this.loader.hide();
          }
          this.ref.markForCheck();
        });
    }
  }

  /*******************************************
   * COMPONENT PUBLIC METHODS
   *******************************************/

  getDefaultReturnRoute(itemDetailStatus: number) {
    const segment = (!this.permissionsService.hasPermission(['admin', 'catalog-edit'])) ?
      'published' : searchPageHelpers.searchSegment(itemDetailStatus);

    const defaultRoute = '/my-products';
    return new ReturnRouteModel(segment ? `${defaultRoute}/${segment}` : defaultRoute, {smlocal: 1});
  }

  getItemDetail(brand: string, id: number) {
    forkJoin([
      this.itemDetailService.fetchItemDetail(brand, id),
      this.priceSheetService.fetchAll(brand),
      this.marketingCopyDetailsService.fetchMarketingCopy(brand, id),
    ])
    .pipe(first())
    .subscribe((result: Array<any>) => {
      this.setData(result[0], result[1], result[2]);
      this.populateSideNavValues(this.itemDetail.status);
      this.productImageUrl = productHelpers.getProductImageUrl(this.itemDetail);
      if (this.isFirstLoad) {
        this.isFirstLoad = false;
        const segment = this.getCurrentSegment();
        if (segment) {
          this.renderSegment(segment.component);
        }
      }

      /*
       * Setup the return route
       */
      this.returnRouteSetup();

      this.populateSideNavValues(this.itemDetail.status);

      this.ref.markForCheck();
    });
  }

  populateSideNavValues(status: number) {
    const values = productHelpers.populateSideNavValues(status, this.prevRouteShort === 'published');
    _.assign(this, values);
  }

  returnRouteSetup() {
      /*
       * Setup the return route
       */
      let returnRoute = null;

      if (this.itemDetail) {
        returnRoute = this.getDefaultReturnRoute(this.itemDetail.status);
      } else {
        returnRoute = this.returnRouteService.peak();
      }

      this.setPageSettings(returnRoute ? returnRoute.route : '');
  }

  getCurrentSegment() {
    const segmentName = this.route.snapshot.url[2].path;
    const segment = _.find(this.navigationSegments, ['segment', segmentName]);
    if (!segment) {
      console.warn(`segment ${segmentName} not found`);
      console.warn(`segment ${event} not found`);
      return;
    }
    return segment;
  }

  /*******************************************
   * COMPONENT PRIVATE METHODS
   *******************************************/

  private setPageSettings(prevRoute: string) {
    const prevRouteShort = this.getPrevPage(prevRoute);
    this.prevRouteShort = prevRouteShort;

    // set header color - deps from previous page
    const indexPageData = indexPagesData[prevRouteShort];
    this.headerColor = indexPageData ? indexPagesData[prevRouteShort].headerColor : 'darkred';
  }

  private getPrevPage(prevRoute: string): any {
    if (!this.permissionsService.hasPermission(['admin', 'catalog-edit'])) {
      return 'published';
    }

    const splittedRoute = prevRoute.split('/');
    let page = splittedRoute.length > 1 ? splittedRoute[2] : null;
    if (prevRoute.indexOf('my-products') === -1 || !page) {
      page = _.get(JSON.parse(localStorage.getItem('myProductsSearchModel')), 'route', '');
    }
    return page;
  }

  private renderSegment(component: Type<any>): void {
    this.componentRef = this.dynamicComponentLoader.load(
      component,
      this.segmentContainer,
      {
        itemDetail: this.itemDetail,
        itemId: this.itemId,
        selectedLanguage: this.selectedLanguage,
        marketingCopy: this.marketingCopy,
      }
    );
    this.ref.markForCheck();
  }

  private setData(itemDetail: any, priceSheets?: any, marketingCopy?: any): void {
    this.itemDetail = setItemDetailValidation(itemDetail);
    this.isItemIvalid = _.some(ecommerceFields, (field: string) => {
      return _.some(_.get(this.itemDetail, 'validation.combinedErrors', []), (item: any) => {
        return item.indexOf(field) !== -1;
      });
    });
    if (priceSheets) { this.priceSheets = priceSheets; }
    if (marketingCopy) { this.marketingCopy = marketingCopy; }
    if (!this.componentRef) { return; }
    const inputs = {
      itemDetail: this.itemDetail,
      marketingCopy: this.marketingCopy,
    };
    _.assign(this.componentRef.instance, inputs);
    _.defer(() => {
      this.componentRef.instance.dataChange.emit(inputs);
    });
  }

  private toggleProductPanel(toggle: boolean) {
    _.defer(() => {
      if (this.productPanel) {
        this.productPanel.toggle(toggle, 'item-details', {panelType: ECOMMERCE_PANEL});
      }
    });
  }

  private closePage() {
    let returnRoute = this.returnRouteService.pop();
    /*
     * Get default route
     */
    if (!returnRoute) {
      returnRoute = this.getDefaultReturnRoute(this.itemDetail.status);
    }
    this.router.navigate([returnRoute.route], { queryParams: returnRoute.queryParams } );
  }
}
